// Variant Stock Handler - Real-time stock status updates
class VariantStockHandler {
  constructor() {
    this.productSelector = document.querySelector('product-selector');
    if (!this.productSelector) return;

    this.productData = this.getProductData();
    this.init();
  }

  init() {
    // Initialize stock status on page load
    this.updateStockStatus();

    // Listen for variant changes
    this.setupVariantListeners();

    // Poll for stock updates every 10 seconds
    this.startStockPolling();
  }

  getProductData() {
    const productJson = this.productSelector.querySelector('[data-product-json]');
    if (!productJson) return null;
    
    try {
      return JSON.parse(productJson.textContent);
    } catch (e) {
      console.error('Error parsing product JSON:', e);
      return null;
    }
  }

  setupVariantListeners() {
    // Listen to all variant option changes (radio buttons and selects)
    const variantOptions = this.productSelector.querySelectorAll('[data-variant-option], [data-variant-select]');
    
    variantOptions.forEach(option => {
      option.addEventListener('change', () => {
        setTimeout(() => this.updateStockStatus(), 100);
      });
    });
  }

  getCurrentVariant() {
    if (!this.productData) return null;

    // Get selected options
    const selectedOptions = [];
    
    // Check radio buttons first
    const radioInputs = this.productSelector.querySelectorAll('[data-variant-option]:checked');
    radioInputs.forEach(input => {
      selectedOptions.push(input.value);
    });

    // Check select dropdowns
    const selectInputs = this.productSelector.querySelectorAll('[data-variant-select]');
    selectInputs.forEach(select => {
      if (select.value && !selectedOptions.includes(select.value)) {
        selectedOptions.push(select.value);
      }
    });

    // Find matching variant
    return this.productData.variants.find(variant => {
      return variant.options.every((option, index) => {
        return option === selectedOptions[index];
      });
    });
  }

  async fetchLatestProductData() {
    try {
      const productUrl = this.productSelector.dataset.url;
      const response = await fetch(`${productUrl}.js`);
      
      if (!response.ok) throw new Error('Failed to fetch product data');
      
      const data = await response.json();
      this.productData = data;
      return data;
    } catch (error) {
      console.error('Error fetching product data:', error);
      return null;
    }
  }

  updateStockStatus() {
    const currentVariant = this.getCurrentVariant();
    if (!currentVariant) return;

    // Update dropdowns
    this.updateDropdownStatus(currentVariant);

    // Update buttons/swatches
    this.updateButtonStatus(currentVariant);
  }

  updateDropdownStatus(currentVariant) {
    const selectWrappers = this.productSelector.querySelectorAll('[data-variant-select-wrapper]');
    
    selectWrappers.forEach(wrapper => {
      const select = wrapper.querySelector('[data-variant-select]');
      if (!select) return;

      const options = select.querySelectorAll('[data-variant-option-value]');
      
      options.forEach(option => {
        const optionValue = option.value;
        const isAvailable = this.isOptionAvailable(select, optionValue);
        
        // DON'T disable option - just add visual indicator
        option.disabled = false; // Keep enabled
        
        // Add visual styling and text indicator
        if (!isAvailable) {
          option.style.opacity = '0.6';
          option.style.color = '#999';
          
          // Clean existing markers first
          const cleanText = option.textContent.replace(' (Out of Stock)', '').replace(' - Out of Stock', '');
          option.textContent = cleanText + ' (Out of Stock)';
          
          // Add data attribute for styling
          option.setAttribute('data-out-of-stock', 'true');
        } else {
          option.style.opacity = '1';
          option.style.color = '';
          
          // Remove out of stock text
          option.textContent = option.textContent.replace(' (Out of Stock)', '').replace(' - Out of Stock', '');
          option.removeAttribute('data-out-of-stock');
        }
      });

      // Fade the wrapper slightly if current variant is out of stock, but keep it clickable
      if (!currentVariant.available) {
        wrapper.style.opacity = '0.7';
        wrapper.classList.add('variant-out-of-stock');
      } else {
        wrapper.style.opacity = '1';
        wrapper.classList.remove('variant-out-of-stock');
      }
    });
  }

  updateButtonStatus(currentVariant) {
    const radioWrappers = this.productSelector.querySelectorAll('.main-product__form-option');
    
    radioWrappers.forEach(wrapper => {
      const input = wrapper.querySelector('[data-variant-option]');
      const label = wrapper.querySelector('label');
      
      if (!input || !label) return;

      const optionValue = input.value;
      const optionName = input.name;
      const isAvailable = this.isOptionAvailableForButtons(optionName, optionValue);

      // Update visual state but DON'T disable
      if (!isAvailable) {
        label.style.opacity = '0.5';
        label.classList.add('variant-out-of-stock');
        wrapper.classList.add('variant-option-out-of-stock');
        
        // Add out of stock indicator
        if (!label.querySelector('.out-of-stock-badge')) {
          const badge = document.createElement('span');
          badge.className = 'out-of-stock-badge';
          badge.textContent = 'Out of Stock';
          label.appendChild(badge);
        }
      } else {
        label.style.opacity = '1';
        label.classList.remove('variant-out-of-stock');
        wrapper.classList.remove('variant-option-out-of-stock');
        
        // Remove out of stock indicator
        const badge = label.querySelector('.out-of-stock-badge');
        if (badge) {
          badge.remove();
        }
      }
    });
  }

  isOptionAvailable(select, optionValue) {
    if (!this.productData) return true;

    // Get all selected options except the one we're checking
    const selectedOptions = this.getSelectedOptionsExcept(select);
    
    // Check if any variant with this option value is available
    return this.productData.variants.some(variant => {
      // Check if this variant matches the option value
      const hasOptionValue = variant.options.includes(optionValue);
      if (!hasOptionValue) return false;

      // Check if it matches other selected options
      const matchesOtherOptions = selectedOptions.every((value, index) => {
        if (value === null) return true; // Skip the option we're checking
        return variant.options[index] === value;
      });

      return matchesOtherOptions && variant.available;
    });
  }

  isOptionAvailableForButtons(optionName, optionValue) {
    if (!this.productData) return true;

    // Get currently selected options
    const selectedOptions = [];
    const allInputs = this.productSelector.querySelectorAll('[data-variant-option]:checked, [data-variant-select]');
    
    allInputs.forEach(input => {
      if (input.type === 'radio' && input.name !== optionName) {
        selectedOptions.push({ name: input.name, value: input.value });
      } else if (input.tagName === 'SELECT') {
        selectedOptions.push({ name: input.name.replace('options[', '').replace(']', ''), value: input.value });
      }
    });

    // Check if any variant with this option is available
    return this.productData.variants.some(variant => {
      const hasOption = variant.options.includes(optionValue);
      if (!hasOption) return false;

      const matchesOthers = selectedOptions.every(selected => {
        return variant.options.includes(selected.value);
      });

      return matchesOthers && variant.available;
    });
  }

  getSelectedOptionsExcept(excludeSelect) {
    const selectedOptions = [];
    const allSelects = this.productSelector.querySelectorAll('[data-variant-select]');
    
    allSelects.forEach((select, index) => {
      if (select === excludeSelect) {
        selectedOptions.push(null);
      } else {
        selectedOptions.push(select.value);
      }
    });

    return selectedOptions;
  }

  startStockPolling() {
    // Update stock status every 10 seconds
    setInterval(async () => {
      await this.fetchLatestProductData();
      this.updateStockStatus();
    }, 10000); // 10 seconds
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new VariantStockHandler();
  });
} else {
  new VariantStockHandler();
}